import mixitup from 'mixitup';

/**
 * Tabs A-1 block functionality.
 *
 * - Desktop: Sidebar tabs with MixItUp filtering
 * - Mobile: Dropdown selector with MixItUp filtering (shared content area)
 * - Supports ?section=N query parameter to open a specific tab on load
 *
 * @function tabsA1
 * @exports tabsA1
 * @module tabsA1
 * @see {@link https://www.kunkalabs.com/mixitup/}
 * @requires mixitup
 */

/**
 * Returns the combined height of sticky top elements (header + WP admin bar).
 *
 * @return {number}
 */
function getTopOffset() {
	let offset = 0;
	const header = document.querySelector('.js-header');
	const wpAdminBar = document.getElementById('wpadminbar');
	if (header) {
		offset += header.offsetHeight;
	}
	if (wpAdminBar) {
		offset += wpAdminBar.offsetHeight;
	}
	return offset;
}

/**
 * Scrolls smoothly to a target element, accounting for sticky header offset.
 *
 * @param {HTMLElement} element - The element to scroll to.
 */
function scrollToElement(element) {
	const targetPosition =
		element.getBoundingClientRect().top +
		window.scrollY -
		getTopOffset() -
		20;

	window.scrollTo({
		top: targetPosition,
		behavior: 'smooth',
	});
}

function blockInit(block) {
	const container = block.querySelector('.tabs-container');
	const buttons = Array.from(
		block.querySelectorAll('.o-tabs-a-1__tab-button'),
	);
	const dropdown = block.querySelector('.o-tabs-a-1__dropdown');

	if (!container || buttons.length === 0) {
		return;
	}

	// Read ?section=N query param (1-indexed)
	const urlParams = new URLSearchParams(window.location.search);
	const sectionParam = parseInt(urlParams.get('section'), 10);
	const hasSectionParam =
		!isNaN(sectionParam) &&
		sectionParam >= 1 &&
		sectionParam <= buttons.length;

	let initialIndex = 0;
	if (hasSectionParam) {
		initialIndex = sectionParam - 1;
	}

	// Set initial active button based on query param or default (first)
	buttons.forEach(btn =>
		btn.classList.remove('o-tabs-a-1__tab-button--active'),
	);
	buttons[initialIndex].classList.add('o-tabs-a-1__tab-button--active');

	// Sync dropdown with initial state
	if (dropdown) {
		dropdown.selectedIndex = initialIndex;
	}

	const defaultFilter =
		buttons[initialIndex]?.getAttribute('data-filter') || 'none';

	// Animation duration in milliseconds
	const animDuration = 140;

	// Initialize MixItUp on the content container
	const mixer = mixitup(container, {
		animation: {
			duration: animDuration,
			effects: 'fade(0.3)',
			easing: 'ease',
		},
		controls: { enable: false },
		load: { filter: defaultFilter },
	});

	/**
	 * Updates the URL query parameter to reflect the active tab.
	 *
	 * @param {number} index - The 1-indexed tab number.
	 */
	function updateURLParam(index) {
		const url = new URL(window.location);
		url.searchParams.set('section', index);
		history.replaceState(null, '', url);
	}

	/**
	 * Fades out a skip-link wrapper (CSS transition via .is-dismissed) and
	 * attaches a one-shot scroll-up watcher that restores it the moment the
	 * user starts scrolling back up (>20px threshold to ignore smooth-scroll
	 * overshoot and trackpad noise).
	 *
	 * @param {HTMLElement} wrapper - The .o-tabs-a-1__skip-link-wrapper element.
	 */
	function dismissSkipLink(wrapper) {
		wrapper.classList.add('is-dismissed');

		let lastY = window.scrollY;
		const onScroll = () => {
			const y = window.scrollY;
			if (y < lastY - 20) {
				wrapper.classList.remove('is-dismissed');
				window.removeEventListener('scroll', onScroll);
				wrapper._detachScroll = null;
				return;
			}
			lastY = y;
		};
		window.addEventListener('scroll', onScroll, { passive: true });

		// Stash detacher so tab-switch reset can clean up if user never scrolls up.
		wrapper._detachScroll = () =>
			window.removeEventListener('scroll', onScroll);
	}

	/**
	 * Resets all dismissed skip-links in this block (used on tab switch).
	 * Also detaches any pending scroll listeners to prevent leaks.
	 */
	function resetDismissedSkipLinks() {
		block
			.querySelectorAll('.o-tabs-a-1__skip-link-wrapper.is-dismissed')
			.forEach(w => {
				if (typeof w._detachScroll === 'function') {
					w._detachScroll();
				}
				w._detachScroll = null;
				w.classList.remove('is-dismissed');
			});
	}

	/**
	 * Handles tab button clicks (desktop sidebar).
	 *
	 * @param {Event} event - The click event on a tab button.
	 */
	function handleTabClick(event) {
		const target = event.target.closest('.filter');
		if (!target) {
			return;
		}

		const filter = target.getAttribute('data-filter');

		// Update active button state
		buttons.forEach(btn =>
			btn.classList.remove('o-tabs-a-1__tab-button--active'),
		);
		target.classList.add('o-tabs-a-1__tab-button--active');

		// Sync dropdown
		if (dropdown) {
			dropdown.value = filter;
		}

		// Reset any dismissed skip-links in other tabs (and detach their
		// scroll listeners) so revisited tabs start fresh.
		resetDismissedSkipLinks();

		// Apply the filter
		mixer.filter('none').then(() => mixer.filter(filter));

		// Update URL
		const newIndex = buttons.indexOf(target) + 1;
		updateURLParam(newIndex);
	}

	// Attach click handler to the desktop sidebar
	const sidebar = block.querySelector('.o-tabs-a-1__sidebar');
	if (sidebar) {
		sidebar.addEventListener('click', handleTabClick);
	}

	// Attach change handler to the mobile dropdown
	if (dropdown) {
		dropdown.addEventListener('change', function() {
			const filter = this.value;
			const newIndex = this.selectedIndex + 1;

			// Sync desktop sidebar active state
			buttons.forEach(btn =>
				btn.classList.remove('o-tabs-a-1__tab-button--active'),
			);
			if (buttons[this.selectedIndex]) {
				buttons[this.selectedIndex].classList.add(
					'o-tabs-a-1__tab-button--active',
				);
			}

			// Reset any dismissed skip-links (and detach their scroll
			// listeners) so revisited tabs start fresh.
			resetDismissedSkipLinks();

			// Apply the filter
			mixer.filter('none').then(() => mixer.filter(filter));

			// Update URL
			updateURLParam(newIndex);
		});
	}

	// Skip-to-button-wrapper click handler.
	// Each skip link lives inside its own .mix content item, so we just find
	// the sibling button-wrapper within the same content item.
	// After scrolling, the skip-link wrapper fades out (dismissSkipLink); it
	// restores itself the moment the user scrolls back up >20px.
	block.querySelectorAll('.js-tab-skip-link').forEach(link => {
		link.addEventListener('click', e => {
			e.preventDefault();
			const item = link.closest('.o-tabs-a-1__content-item');
			const target = item?.querySelector('.o-tabs-a-1__button-wrapper');
			if (target) {
				scrollToElement(target);
			}
			const wrapper = link.closest('.o-tabs-a-1__skip-link-wrapper');
			if (wrapper) {
				dismissSkipLink(wrapper);
			}
		});
	});

	// If ?section= param is present, scroll to the block
	if (hasSectionParam) {
		const blockSection = block.closest('section');
		if (blockSection) {
			setTimeout(() => {
				scrollToElement(blockSection);
			}, 500);
		}
	}
}

export default function tabsA1() {
	const blocks = document.querySelectorAll("[data-module='tabsA1']");
	blocks.forEach(block => blockInit(block));
}
